"use client";

import { cn } from "@/lib/utils";
import { memo, type CSSProperties } from "react";

type RippleProps = {
  /** Diameter of the innermost circle in pixels. */
  mainCircleSize?: number;
  /** Starting opacity of the innermost circle (others fade further). */
  mainCircleOpacity?: number;
  /** How many concentric circles to render. */
  numCircles?: number;
  /** Diameter increment between consecutive circles, in pixels. */
  circleStep?: number;
  /** Per-circle opacity decrement (clamped at 0). */
  opacityStep?: number;
  className?: string;
  /** Override the ring color (any CSS color). Defaults to a brand-blue at low alpha. */
  color?: string;
};

/**
 * Ripple - concentric pulsing rings centered in the parent. Mirrors the
 * Magic UI `ripple` component (magicui.design/docs/components/ripple) but
 * relies on the `animate-ripple` keyframes defined in globals.css.
 *
 * Render inside a `relative` parent. The component is `absolute inset-0`,
 * pointer-events disabled, and centers its circles.
 */
export const Ripple = memo(function Ripple({
  mainCircleSize = 210,
  mainCircleOpacity = 0.24,
  numCircles = 8,
  circleStep = 70,
  opacityStep = 0.03,
  className,
  color,
}: RippleProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 select-none",
        className,
      )}
    >
      {Array.from({ length: numCircles }).map((_, i) => {
        const size = mainCircleSize + i * circleStep;
        const opacity = Math.max(mainCircleOpacity - i * opacityStep, 0);
        const animationDelay = `${i * 0.06}s`;
        const borderStyle = i === numCircles - 1 ? "dashed" : "solid";

        const style: CSSProperties = {
          width: `${size}px`,
          height: `${size}px`,
          opacity,
          animationDelay,
          borderStyle,
          // Allow callers to override the ring tint; defaults to a translucent
          // white so the component works on light or dark surfaces alike.
          borderColor: color ?? "hsl(0 0% 100% / 0.5)",
          // `currentColor` would also work, but explicit borderColor keeps the
          // override path obvious.
          top: "50%",
          left: "50%",
          // Center, then animate via the `animate-ripple` keyframes which
          // include a translate(-50%, -50%) baseline transform.
          transform: "translate(-50%, -50%) scale(1)",
        };

        return (
          <div
            key={i}
            className="absolute animate-ripple rounded-full border bg-transparent shadow-xl [animation-iteration-count:infinite] [animation-duration:3.4s]"
            style={style}
          />
        );
      })}
    </div>
  );
});
